import { CloseOutlined } from '@ant-design/icons';
import { Drawer } from '@dynamika-shared/ui/Drawer/Drawer';
import { FC, useCallback, useEffect, useMemo, ChangeEvent } from 'react';
import { Divider } from 'antd';
import { Select } from '@dynamika-shared/ui/Select/Select';
import { HeaderInfo } from './HeaderInfo';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { TextArea } from '@dynamika-shared/ui/TextArea/TextArea';
import { useClinicalTrialContextSlice } from '@restore/clinicalTrialContext/clinicalTrialContext.slice';
import { useQCTaskPatientSlice } from '@restore/QCTaskPatient/QCTaskPatient.slice';
import { useLabelsHook } from 'src/pages/QC/QCLabels/useLabels.hook';
import { useAppSelector } from '@hooks/redux';
import { selectAuthView } from '@restore/auth/auth.slice';
import { QueryEvent } from '@restore/queries/queries.types';
import { useQCQueriesSlice } from '@restore/qcQueries/qcQueries.slice';
import { useQCTaskSlice } from '@restore/QCTask/QCTask.slice';
import { useQCTaskUploadsListSlice } from '@restore/QCTaskUploadsList/QCTaskUploadsList.slice';
import { AttachFileButton } from '@components/Queries/QueryPage/components/AttachFileButton';
import { SelectedQueryItems } from '@components/Queries/QueryPage/components/SelectedQueryItems';
import { MAX_SIZE, useAttachments } from '@components/Queries/hooks/useAttachments';
import { querySchema } from 'src/pages/QC/QCSubjectQueries/components/OpenQueryDrawer/schema';
import { HiddenTag } from '@components/SelectLabels/SelectLabelsOptionRender';
import { QUERY_TYPES } from 'src/pages/QC/QCSubjectQueries/components/OpenQueryDrawer/queryTypes';

type OpenQueryDrawerProps = {
  onClose: () => void;
};

type OpenQueryDrawerState = {
  types: string[];
  recipients: string[];
  queryText: string;
  attachments: File[];
};

const formChangeOptions = {
  shouldDirty: true,
  shouldTouch: true,
  shouldValidate: true,
};

const defaultValues = {
  types: [],
  recipients: [],
  queryText: '',
  attachments: [],
};

export const OpenQueryDrawer: FC<OpenQueryDrawerProps> = ({ onClose }) => {
  const methods = useForm<OpenQueryDrawerState>({
    mode: 'onChange',
    resolver: yupResolver(querySchema),
    defaultValues,
  });
  const {
    watch,
    setValue,
    handleSubmit,
    formState: { errors, isValid, isDirty },
  } = methods;

  const { onAttachmentsChange, attachments, onRemoveAttachment, validateFile } =
    useAttachments<OpenQueryDrawerState>({
      setValue,
      watch,
      field: 'attachments',
    });

  const config = useClinicalTrialContextSlice(s => s.data?.configuration);
  const patient = useQCTaskPatientSlice(s => s.data);
  const qcTask = useQCTaskSlice(s => s.data);
  const uploads = useQCTaskUploadsListSlice(s => s.data);
  const { uuid } = useAppSelector(selectAuthView);
  const [recipients, fetchQCQueryRecipients, createQCQuery] = useQCQueriesSlice(
    (s, a) => [s.recipients, a.fetchQCQueryRecipients, a.createQCQuery]
  );

  const { currentVisit } = useLabelsHook();

  const headerSiteCode = useMemo(
    () => config?.sites?.find(s => s.id === patient?.siteId)?.code ?? '',
    [patient?.siteId, config?.sites]
  );

  const hiddenTagRender = () => {
    return <HiddenTag />;
  };

  const onSelectRecipient = (value: string) => {
    const current = watch('recipients') ?? [];
    if (!current.includes(value)) {
      setValue('recipients', [...current, value], formChangeOptions);
    }
  };

  const onDeselectRecipient = useCallback((value: string) => {
    const current = watch('recipients');
    const updated = current.filter(v => v !== value);
    setValue('recipients', updated, formChangeOptions);
  }, []);

  const handleQueryTextChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    setValue('queryText', e.target.value, formChangeOptions);
  };

  useEffect(() => {
    if (config?.id) {
      fetchQCQueryRecipients(config.id);
    }
  }, [config?.id]);

  const queryTypesOptions = useMemo(
    () => QUERY_TYPES.map(type => ({ value: type, label: type })),
    []
  );
  const recipientOptions = useMemo(
    () =>
      recipients?.map(recipient => ({
        value: recipient.id,
        label: `${recipient.fullName} (${recipient.email})`,
      })) ?? [],
    [recipients]
  );

  const selectedRecipientLabels = watch('recipients')?.map(
    id => recipientOptions.find(opt => opt.value === id)?.label ?? id
  );

  const handleCloseRecipient = useCallback(
    (label: string) => {
      const option = recipientOptions.find(opt => opt.label === label);
      onDeselectRecipient(option?.value ?? label);
    },
    [recipientOptions, onDeselectRecipient]
  );

  const onSelectQueryType = (value: string) => {
    const current = watch('types') ?? [];
    if (!current.includes(value)) {
      setValue('types', [...current, value], formChangeOptions);
    }
  };

  const onDeselectQueryType = useCallback((value: string) => {
    const current = watch('types');
    const updated = current.filter(v => v !== value);
    setValue('types', updated, formChangeOptions);
  }, []);

  const selectedQueryTypeLabels = watch('types')?.map(
    type => queryTypesOptions.find(opt => opt.value === type)?.label ?? type
  );

  const handleCloseQueryType = useCallback(
    (label: string) => {
      const option = queryTypesOptions.find(opt => opt.label === label);
      onDeselectQueryType(option?.value ?? label);
    },
    [queryTypesOptions, onDeselectQueryType]
  );

  const onSubmit = (data: OpenQueryDrawerState) => {
    const currentUserId = uuid ?? '';
    const siteId = qcTask?.siteId ?? '';
    const patientId = qcTask?.patientId ?? '';
    const visitId = qcTask?.visitId ?? '';
    const uploadId = uploads?.[0]?.taskUploadId ?? '';

    const payload = {
      siteId,
      patientId,
      visitId,
      uploadId,
      author: currentUserId,
      types: data.types,
      recipients: data.recipients,
      message: {
        event: QueryEvent.SUBMIT,
        actedBy: currentUserId,
        message: data.queryText,
        attachments: data.attachments,
      },
    };
    createQCQuery(payload);
    onClose();
  };

  return (
    <Drawer
      id={'qc-open-query-drawer'}
      classNames={{
        header:
          '!px-0 !pb-[8px] !pt-[8px] mb-2 !border-0 [&_button]:absolute [&_button]:top-[1.3rem] [&_button]:right-[0.4rem]',
        body: 'flex flex-col !ps-0 !pe-2 !pb-0 !pt-[5px] -me-2',
        content: 'rounded-lg bg-white',
        footer: '!px-0',
      }}
      title={'Open Query'}
      open={true}
      width={928}
      onClose={onClose}
      placement='right'
      closeIcon={<CloseOutlined />}
      footer={
        <div className='flex justify-between items-center gap-3'>
          <Button className='w-full' onClick={onClose}>
            Cancel
          </Button>
          <Button
            className='w-full'
            type='primary'
            onClick={handleSubmit(onSubmit)}
            disabled={!isDirty || !isValid}
          >
            Confirm
          </Button>
        </div>
      }
    >
      <FormProvider {...methods}>
        <HeaderInfo
          studyName={config?.name ?? ''}
          site={headerSiteCode}
          subject={patient?.patientCode ?? ''}
          visit={currentVisit?.visitName ?? ''}
        />
        <h2 className='p-1.5 text-1824 font-medium bg-body rounded-[8px] mt-4 mb-3'>
          Query Type
        </h2>
        <Select
          mode='multiple'
          tagRender={() => hiddenTagRender()}
          inOneLine
          placeholder='Select query type'
          label='Query Type'
          required
          classNameLabel={'ps-1.5'}
          onSelect={onSelectQueryType}
          onDeselect={onDeselectQueryType}
          value={watch('types')}
          name='types'
          options={queryTypesOptions}
          errors={errors.types?.message}
        />
        <div className='flex gap-1.5 mt-2'>
          <div className='w-[50%]' />
          <SelectedQueryItems
            selectedItems={selectedQueryTypeLabels}
            onClose={handleCloseQueryType}
          />
        </div>
        <div>
          <h2 className='p-1.5 text-1824 font-medium bg-body rounded-[8px] mt-4 mb-3'>
            Recipients
          </h2>
          <Select
            mode='multiple'
            tagRender={() => hiddenTagRender()}
            inOneLine
            placeholder='Select recipient(s)'
            label='To'
            required
            classNameLabel={'ps-1.5'}
            onSelect={onSelectRecipient}
            onDeselect={onDeselectRecipient}
            value={watch('recipients')}
            name='recipients'
            options={recipientOptions}
            errors={errors.recipients?.message}
          />
          <div className='flex gap-1.5 mt-2'>
            <div className='w-[50%]' />
            <SelectedQueryItems
              selectedItems={selectedRecipientLabels}
              onClose={handleCloseRecipient}
            />
          </div>
          <h2 className='p-1.5 text-1824 font-medium bg-body rounded-[8px] mt-4 mb-3'>
            Query Information
          </h2>
          <TextArea
            className='!resize-none'
            classNameLabel='ps-1.5'
            placeholder='Type here'
            label='Query Text'
            rows={5}
            required
            inOneLine
            onChange={handleQueryTextChange}
            value={watch('queryText')}
            errors={errors.queryText?.message}
          />
          <Divider className={'bg-brightGray'} />
          <div className='flex justify-between items-center'>
            <div>
              <h3 className='text-chineseBlack text-1420 font-semibold'>
                Attachments
              </h3>
              <span className='text-dimGray text-1216 font-medium'>
                Only .jpg, .png, .pdf, .doc and .docx formats are supported.{' '}
                <br /> File must not exceed 5 MB
              </span>
            </div>
            <div>
              <AttachFileButton onChange={onAttachmentsChange}>
                Attach File
              </AttachFileButton>
            </div>
          </div>
          <div className='flex gap-1.5 mt-3 text-1216 text-dimGray'>
            <div className='w-[50%]' />
            <SelectedQueryItems
              selectedFiles={attachments as File[]}
              onCloseFile={onRemoveAttachment}
              getExtraFileTagClassName={file =>
                file.size > MAX_SIZE || !validateFile(file)
                  ? 'bg-redOrange/10'
                  : ''
              }
            />
          </div>
          {errors.attachments?.message && (
            <div className='text-redOrange text-1420 font-normal mt-1 ps-[3.75rem]'>
              {errors.attachments.message}
            </div>
          )}
          <Divider className={'bg-brightGray mt-8'} />
        </div>
      </FormProvider>
    </Drawer>
  );
};
